import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Connects to data-controller="sso"
export default class extends Controller {
  static targets = ["email", "submit"]
  static values = {
    domain: Array,
    checkUrl: { type: String, default: "/accounts/sso_check" },
  }

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  refresh() {
    // Clear any pending check
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    // Debounce: wait 300ms after user stops typing
    this.debounceTimer = setTimeout(() => this.checkSso(), 300)
  }

  // Checks if the entered email requires SSO authentication.
  //
  // Some accounts with SSO domains may have sso_bypass enabled,
  // allowing them to use password login instead of SSO. We can't determine this
  // client-side since the bypass flag is stored in the database, so we need to
  // ask the server whether to redirect to SSO or stay on the password login form.
  async checkSso() {
    if (!this.hasEmailTarget || !this.hasSubmitTarget) return

    const email = this.emailTarget.value
    if (!email || !email.includes("@")) return

    const normalized = email.trim().toLowerCase()

    // Quick client-side check first (skip API if domain doesn't match)
    const domainMatches = this.domainValue.some((domain) =>
      normalized.endsWith(domain)
    )
    if (!domainMatches) return

    // Check server for sso_bypass
    try {
      const response = await post(this.checkUrlValue, {
        body: JSON.stringify({ email: normalized }),
        contentType: "application/json",
        responseKind: "json",
      })

      if (response.ok) {
        const data = await response.json
        if (data.sso_required) {
          this.element.requestSubmit(this.submitTarget)
        }
      }
    } catch {
      // On error, fall back to redirect (original behavior)
      this.element.requestSubmit(this.submitTarget)
    }
  }
}
